<template>
  <div>
    <div class="mxw-300 mx-auto">
      <v-btn
        @click="open"
        color="primary"
        prepend-icon="add"
        size="x-large"
        block
        rounded
      >
        {{ $bct('Request') }}
      </v-btn>
    </div>

    <v-dialog
      v-model="newDialogIsVisible"
      max-width="600"
      persistent
    >
      <v-card
        border
        flat
        tile
      >
        <v-card-title>{{ $bct('New request') }}</v-card-title>

        <v-divider class="mb-6" />

        <v-card-text>
          <v-row
            class="mb-4"
            dense
          >
            <v-col
              class="fs-16"
              cols="12"
            >
              <span>
                {{ $bct('Location') }}
              </span>
            </v-col>
            <v-col cols="12">
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="providerId"
                @update:model-value="loadAssessmentPrograms"
                :aria-label="$bct('Location')"
                :items="providers"
                :placeholder="$bct('Select a location')"
                item-title="name"
                item-value="id"
                variant="filled"
                hide-details
                hide-no-data
              />
            </v-col>
          </v-row>

          <v-row
            class="mb-2"
            dense
          >
            <v-col
              class="labeled-input"
              cols="12"
            >
              {{ $bct('Type') }}
            </v-col>
            <v-col cols="12">
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="assessmentProgram"
                :aria-label="$bct('Type')"
                :items="assessmentPrograms"
                item-title="name"
                item-value="id"
                variant="filled"
                hide-details
                return-object
              />
            </v-col>
          </v-row>

          <v-alert
            v-if="assessmentProgram?.provider_request_disclaimer"
            color="primary"
            type="info"
          >
            <span>
              {{ $bct(assessmentProgram.provider_request_disclaimer) }}
            </span>
          </v-alert>
        </v-card-text>

        <v-divider class="mt-4 mb-6" />

        <v-card-actions>
          <v-btn
            @click="newDialogIsVisible = false"
            size="x-large"
            variant="text"
          >
            {{ $bct('Cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            @click="draft()"
            :disabled="!providerId || !assessmentProgram"
            :loading="processing"
            color="primary"
            size="x-large"
          >
            {{ $bct('Create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template v-if="assessmentProgram?.request_form_schema_id">
      <FormDialog
        @save="create"
        ref="requestDialog"
        :provider-id="providerId"
        :schema-id="assessmentProgram.request_form_schema_id"
      />
    </template>
  </div>
</template>

<script setup>
import { sortBy } from 'lodash';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import FormDialog from '@/shared/components/form/FormDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');

const { api } = useApi();
const eventBus = useEventBus();
const router = useRouter();
const store = useStore();

const assessmentProgram = ref(null);
const assessmentPrograms = ref([]);
const providerId = ref(null);
const newDialogIsVisible = ref(false);
const processing = ref(false);
const providers = ref([]);

// Template refs
const requestDialog = ref(null);

watch(providerId, (newValue, _oldValue) => loadAssessmentPrograms(newValue));

function create(form) {
  processing.value = true;
  api.manager.assessment.create(
    { provider_id: providerId.value, schema_id: assessmentProgram.value.id },
    async (response) => {
      processing.value = false;
      newDialogIsVisible.value = false;

      if (form) {
        api.manager.provider.form.associate(
          providerId.value,
          form.id,
          { assessment_id: response.data.id },
          async () => {
            await router.push({
              name: 'AssessmentShow',
              params: { assessmentId: response.data.id },
            });
          },
        );
      } else {
        await router.push({ name: 'AssessmentShow', params: { assessmentId: response.data.id } });
      }
    },
    (error) => {
      processing.value = false;
      eventBus.error(error);
    },
  );
}

function draft() {
  const failedCriteria = assessmentProgram.value?.eligibility
    .filter((eligibility) => !eligibility.eligible)
    .map((eligibility) => eligibility.message);

  if (failedCriteria.length > 0) return eventBus.error(failedCriteria);

  if (assessmentProgram.value?.request_form_schema_id) {
    requestDialog.value.open();
  } else {
    create();
  }

  return null;
}

function open() {
  providerId.value = null;
  assessmentProgram.value = null;
  newDialogIsVisible.value = true;
}

async function load() {
  const response = await api.manager.provider.index();
  providers.value = response?.data || [];
}

async function loadAssessmentPrograms(providerId) {
  const response = await api.manager.provider.assessment_program.index(providerId);
  assessmentPrograms.value = response.data
    .filter((assessmentProgram) => !!store.state.schemas[assessmentProgram.id])
    .filter(
      (assessmentProgram) =>
        store.state.schemas[assessmentProgram.id].meta.enable_provider_requests,
    );

  assessmentPrograms.value = sortBy(assessmentPrograms.value, [(assessment) => assessment.name]);
}

onMounted(load);
</script>
